<template>
  <div class="step-review">
    <div class="step-review__rows">
      <div class="step-review__row">
        <label class="step-review__row-label">Campaign</label>
        <span v-if="name" class="step-review__row-value text-white">{{ name }}</span>
        <a v-else href="#" class="step-review__row-fix text-warning" @click.prevent="$emit('navigate', 'general')">
          <i class="tim-icons icon-alert-circle-exc mr-1"></i>Add a campaign name
        </a>
        <a v-if="name" href="#" class="step-review__row-edit" @click.prevent="$emit('navigate', 'general')">Edit</a>
      </div>

      <div class="step-review__row">
        <label class="step-review__row-label">Audience</label>
        <span class="step-review__row-value">
          <template v-if="audienceChips.length">
            <badge v-for="(chip, i) in audienceChips" :key="i" type="primary" class="mr-1">{{ chip }}</badge>
          </template>
          <span v-else class="text-white">All subscribed customers</span>
        </span>
        <a href="#" class="step-review__row-edit" @click.prevent="$emit('navigate', 'general')">Edit</a>
      </div>

      <div class="step-review__row">
        <label class="step-review__row-label">Recipients</label>
        <span v-if="previewCount === 0 && !previewLoading && !ongoing" class="step-review__row-value text-warning">
          <i class="tim-icons icon-alert-circle-exc mr-1"></i>No customers match the current filters
        </span>
        <span v-else class="step-review__row-value text-white">
          <i v-if="previewLoading" class="el-icon-loading step-review__loader"></i>
          <template v-else-if="ongoing && previewCount !== null">
            <strong>{{ previewCount.toLocaleString() }}</strong> customers will be reached, one email each on their day
          </template>
          <template v-else-if="previewCount !== null">
            approximately <strong>{{ previewCount.toLocaleString() }}</strong>, recalculated at send time
          </template>
          <template v-else>recalculated at send time</template>
        </span>
      </div>

      <div v-if="estimatedCost" class="step-review__row">
        <label class="step-review__row-label">Estimated charge</label>
        <span class="step-review__row-value text-white">{{ estimatedCost }}, based on the final recipient count at send time</span>
      </div>

      <div class="step-review__row">
        <label class="step-review__row-label">Template</label>
        <span v-if="templateName" class="step-review__row-value text-white">{{ templateName }}</span>
        <a v-else href="#" class="step-review__row-fix text-warning" @click.prevent="$emit('navigate', 'template')">
          <i class="tim-icons icon-alert-circle-exc mr-1"></i>Pick a template
        </a>
        <a v-if="templateName" href="#" class="step-review__row-edit" @click.prevent="$emit('navigate', 'template')">Edit</a>
      </div>

      <div class="step-review__row">
        <label class="step-review__row-label">Schedule</label>
        <span class="step-review__row-value text-white">{{ scheduleDisplay }}</span>
        <a href="#" class="step-review__row-edit" @click.prevent="$emit('navigate', 'send')">Edit</a>
      </div>
    </div>

    <label class="d-block mt-4 mb-2">Before it goes out</label>
    <ul class="step-review__checklist">
      <li v-for="item in checklist" :key="item.key" class="step-review__check">
        <i :class="checkIcon(item.level)"></i>
        <span>{{ item.text }}</span>
      </li>
    </ul>

    <div class="step-review__test">
      <label class="d-block">Send a test to yourself first</label>
      <div class="step-review__test-group">
        <base-input class="step-review__test-wrapper mb-0">
          <el-input
            :value="testEmail"
            placeholder="you@example.com"
            @input="$emit('update:test-email', $event)"
          />
        </base-input>
        <base-button
          type="primary"
          size="sm"
          class="m-0 step-review__test-button"
          :disabled="testSending || !templateId"
          @click="$emit('send-test')"
        >
          <i v-if="testSending" class="el-icon-loading mr-1"></i>
          Send test
        </base-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type PropType } from 'vue'
import { Badge } from 'src/components'

defineProps({
  // ongoing campaigns recount every day, so zero today is not a warning
  ongoing: { type: Boolean, default: false },
  name: { type: String, default: '' },
  audienceChips: { type: Array as PropType<string[]>, default: () => [] },
  previewCount: { type: Number as PropType<number | null>, default: null },
  previewLoading: { type: Boolean, default: false },
  estimatedCost: { type: String, default: '' },
  templateId: { type: Number as PropType<number | null>, default: null },
  templateName: { type: String, default: '' },
  scheduleDisplay: { type: String, default: 'Send now' },
  checklist: { type: Array as PropType<Array<{ key: string; level: string; text: string }>>, default: () => [] },
  testEmail: { type: String, default: '' },
  testSending: { type: Boolean, default: false }
})

defineEmits(['update:test-email', 'send-test', 'navigate'])

const checkIcon = (level: string) => {
  if (level === 'pass') return 'tim-icons icon-check-2 text-success'
  if (level === 'warn') return 'tim-icons icon-alert-circle-exc text-warning'
  return 'tim-icons icon-alert-circle-exc text-danger'
}
</script>

<script lang="ts">
export default { name: 'campaign-step-review' }
</script>

<style lang="scss" scoped>
// Layout only. Badges, info hints, buttons, inputs, and state colors are
// theme primitives; the hairline uses the theme-neutral gray.
.step-review {
  &__row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 0.7rem 0;

    & + & {
      border-top: 1px solid rgba(125, 125, 125, 0.12);
    }
  }

  &__row-label {
    flex-shrink: 0;
    width: 110px;
    margin: 0;
  }

  &__row-value {
    flex: 1;
    min-width: 0;
  }

  &__row-fix {
    flex: 1;

    &:hover {
      text-decoration: underline;
    }
  }

  &__row-edit {
    flex-shrink: 0;
    font-size: 0.75rem;

    &:hover {
      text-decoration: underline;
    }
  }

  &__loader {
    color: #e14eca;
  }

  &__checklist {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__check {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 0.35rem 0;

    i {
      font-size: 0.875rem;
      flex-shrink: 0;
    }
  }

  &__test {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(125, 125, 125, 0.15);
  }

  // Input/button group, same construction as the amount-input-group pattern
  // (TransactionDrawer): one bordered container, inner controls borderless.
  &__test-group {
    display: flex;
    align-items: stretch;
    max-width: 400px;
    border: 1px solid rgba(225, 78, 202, 0.4);
    border-radius: 0.4285rem;
    overflow: hidden;

    &:focus-within {
      border-color: #e14eca;
    }
  }

  &__test-wrapper {
    flex: 1;

    ::v-deep .el-input__inner {
      border: none;
      border-radius: 0;
      background: transparent;
      height: 100%;
    }
  }

  &__test-button {
    border-radius: 0;
    flex-shrink: 0;
  }
}
</style>
